import React from "react";
import * as stylex from "@stylexjs/stylex";
import { useMutation } from "urql";
import { timeAgo } from "~/utils/timeAgoLocaleEn";
import ProjectIcon from "~/icons/ProjectIcon";
import type { Project } from "~/types";
import { DeleteProjectAttachmentMutation, type IDeleteProjectAttachmentMutation } from "../queries";
import "../styles/variables.stylex";
import { ZendeskThemeVariables } from "../styles/variables.stylex";
import { FullViewPrompt } from "./FullViewPrompt";
type Props = {
  /** The ID of the Project attachment. */
  attachmentId?: string;
  /** The project to be shown. */
  project: Project;
  /** Whether unlinking is allowed. */
  allowUnlink?: boolean;
  /** Callback to be executed after unlinking the attachment. */
  onUnlink?(): void;
};

/**
 * Renders the linked-project summary for a Zendesk ticket, including status, priority, and optional unlink actions.
 *
 * Use this in the same places as {@link IssueWidget} when the workspace supports project links in addition to
 * issue links.
 */
export const ProjectWidget = (props: Props) => {
  const {
    attachmentId,
    project,
    allowUnlink,
    onUnlink
  } = props;
  const createdAt = new Date(project.createdAt);
  const updatedAt = new Date(project.updatedAt);
  const [confirmUnlink, setConfirmUnlink] = React.useState(false);
  const [_deleteAttachmentResult, deleteAttachment] = useMutation<IDeleteProjectAttachmentMutation>(DeleteProjectAttachmentMutation);
  const handleUnlink = (event: React.MouseEvent) => {
    event.preventDefault();
    setConfirmUnlink(true);
  };
  const processUnlink = () => {
    if (attachmentId && onUnlink) {
      setConfirmUnlink(false);
      void deleteAttachment({
        id: attachmentId
      }).then(onUnlink);
    }
  };
  return <div className="sx-78zum5 sx-dt5ytf">
      {confirmUnlink && <FullViewPrompt title="Unlink this project?" description="Linear project won't be deleted." confirmLabel="Unlink project" onConfirm={processUnlink} onCancel={() => setConfirmUnlink(false)} />}
      <div className="sx-78zum5 sx-1qughib sx-od5an3">
        <div className="sx-78zum5 sx-6s0dn4 sx-17d4w8g">
          <ProjectIcon />
          <a href={project.url} target="_blank" className="sx-1s688f">
            {project.identifier ?? project.name}
          </a>
        </div>
        <div>
          {allowUnlink && <>
              <a onClick={handleUnlink} href="" className="sx-fifm61">
                Unlink
              </a>
              <span className="sx-t970qd sx-1xijbu4 sx-8qomae">&middot;</span>
            </>}

          <a href={project.url} target="_blank" className="sx-fifm61">
            View in Linear
          </a>
        </div>
      </div>
      <div className="sx-k50ysn sx-od5an3">{project.name}</div>
      <div className="sx-78zum5 sx-dt5ytf sx-fifm61 sx-od5an3">
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-nnjuj9">Status</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m">{project.status.name}</div>
        </div>
        <div className="sx-78zum5 sx-so031l sx-1q0q8m5 sx-1f0u6lw">
          <div className="sx-78zum5 sx-6s0dn4 sx-1t0vds8 sx-5k8d2m sx-nnjuj9">Priority</div>
          <div className="sx-78zum5 sx-98rzlu sx-6s0dn4 sx-1t0vds8 sx-5k8d2m">{project.priorityLabel}</div>
        </div>
      </div>
      <span className="sx-fifm61 sx-8qomae">Created {timeAgo.format(createdAt, "round")}</span>
      {project.createdAt !== project.updatedAt && <span className="sx-fifm61 sx-8qomae">Updated {timeAgo.format(updatedAt, "round")}</span>}
    </div>;
};